import { useEffect, useRef } from 'react';
import { prefetchAround } from '../../services/tilePrefetch';
import { ensureHydrated, getTopCells, HOT_THRESHOLD } from '../../services/regionUsageService';
import { cellKeyToBounds } from '../../utils/grid';

interface HotRegionWarmerProps {
  /** Master-flagg (feature-flagg fra constants). */
  enabled: boolean;
  /** Leaflet-URL-maler for de aktive baselagene. */
  urlTemplates: string[];
  /** Forsinkelse etter mount før oppvarming (la initiell last få ro først). */
  delayMs?: number;
  /** Zoom å varme opp den hyppige cellen på (regionalt nivå). */
  warmZoom?: number;
}

/**
 * Varmer opp brukerens MEST brukte kartområde ved oppstart: prefetcher fliser for
 * den hotteste cellen (regionUsageService) på umålt nett, én gang, litt etter mount.
 *
 * Hvorfor: #1 beskytter hot fliser mot eviction MENS de er i cachen, men etter en
 * lang tur i andre områder (eller på en ny enhet) kan hjemmeområdet være tomt. Da
 * er dette området klart før du åpner kartet der neste gang. Cache-first → allerede-
 * varme fliser koster ingenting; unmetered-gated i prefetchAround (aldri mobildata).
 * Rendres inne i <MapContainer> men bruker ikke kart-state (uavhengig av viewport).
 */
const HotRegionWarmer: React.FC<HotRegionWarmerProps> = ({
  enabled,
  urlTemplates,
  delayMs = 8000,
  warmZoom = 13,
}) => {
  const ranRef = useRef(false);
  const templatesKey = urlTemplates.join('|');

  useEffect(() => {
    if (!enabled || urlTemplates.length === 0 || ranRef.current) return;
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      ranRef.current = true;
      await ensureHydrated();
      const top = getTopCells(1)[0];
      if (!top || top.count < HOT_THRESHOLD) return; // ingen tydelig hjemmeområde ennå
      const bounds = cellKeyToBounds(top.key);
      if (!bounds) return;
      void prefetchAround(urlTemplates, bounds, warmZoom, ac.signal, { maxTiles: 40 });
    }, delayMs);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, templatesKey, delayMs, warmZoom]);

  return null;
};

export default HotRegionWarmer;
